/**
 * Video Comprehension Layer — client SDK ("the spine").
 *
 * Owns one View: mints view/session/seq, batches events, and beacons them to
 * POST /api/analytics/collect. Cookieless T0 capture runs ALWAYS — it's lawful
 * without consent (no device storage, server drops the IP after hashing), per
 * the owner's "capture-maximal, escalate-identity-when-legal" stance. It does
 * NOT gate on the GA4 `analytics_consent` flag.
 *
 * Transport is fire-and-forget `text/plain` so cross-origin embed beacons stay
 * CORS-preflight-free. The unload path beacons a final `view_end` + the full
 * coverage vector. Self-manages Page-Visibility, pagehide, and the §8.2
 * slide→parent postMessage listener; the host hook drives playback events.
 *
 * Design: docs/engineering/video-comprehension-layer-design-2026-06-20.md
 */

import { v4 as uuidv4 } from 'uuid';
import {
  isSlideAnalyticsMessage,
  type AnalyticsContext,
  type AnalyticsEnvelope,
  type AnalyticsEventName,
  type AnalyticsEventProperties,
  type AnalyticsVariant,
  type SlideAnalyticsMessage,
} from '@agentic-ivy/shared-types';

export interface SpineConfig {
  shareToken: string;
  /** Public project key — the share token (server joins to the real project). */
  projectId: string;
  variant: AnalyticsVariant;
  /** Absolute collect endpoint, e.g. `${VITE_API_URL}/api/analytics/collect`. */
  collectUrl: string;
  /** The page url to report instead of location.href (a private link's url carries a credential). */
  pageUrl?: string;
}

const FLUSH_SIZE = 30;
const FLUSH_MS = 2000;
const MAX_QUEUE = 500; // backpressure cap if the endpoint is unreachable

export class AnalyticsSpine {
  private readonly cfg: SpineConfig;
  private readonly viewId = uuidv4();
  private readonly sessionId = uuidv4();
  private seq = 0;

  private queue: AnalyticsEnvelope[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  /** Per-second coverage counts (§7); index = whole second, value = play count. */
  private coverage: number[] = [];
  private lastTickSec = -1;

  // Watch-metric accumulators (fed from the player via progress()).
  private durationMs = 0;
  private maxPlayheadMs = 0;
  private playingMs = 0;
  private playingSince: number | null = null;

  /**
   * Opaque provider-launch identity from the URL launch context (§8.3). For
   * provider-launched vplay shares `window.__ivyLaunchContext.actor` keys the
   * analytics rows and downstream egress; absent ⇒ null ⇒ anonymous playback,
   * no egress. Read once at construction (the launch context is frozen).
   */
  private readonly identifiedId: string | null;
  private readonly registration: string | null;
  /**
   * Launch signature proving `identifiedId` was issued by us for this share.
   * Forwarded verbatim; the server verifies. Null ⇒ unsigned launch, which is
   * still accepted for providers that do not require signatures.
   */
  private readonly sig: string | null;

  private ctxCache: AnalyticsContext | null = null;
  private allowedMessageOrigins: Set<string>;
  private started = false;
  private ended = false;

  private readonly onMessage = (e: MessageEvent) => this.handlePostMessage(e);
  private readonly onVisibility = () => this.handleVisibility();
  private readonly onPageHide = () => this.finalize('pagehide');

  constructor(cfg: SpineConfig) {
    this.cfg = cfg;
    const lc = (
      window as {
        __ivyLaunchContext?: {
          actor?: string | null;
          reg?: string | null;
          sig?: string | null;
        };
      }
    ).__ivyLaunchContext;
    this.identifiedId = typeof lc?.actor === 'string' && lc.actor.length > 0 ? lc.actor : null;
    this.registration = typeof lc?.reg === 'string' && lc.reg.length > 0 ? lc.reg : null;
    this.sig = typeof lc?.sig === 'string' && lc.sig.length > 0 ? lc.sig : null;
    const origins = new Set<string>([window.location.origin]);
    try {
      origins.add(new URL(cfg.collectUrl).origin);
    } catch {
      /* ignore malformed url */
    }
    this.allowedMessageOrigins = origins;
  }

  /** Begin the view: register listeners and emit the impression. */
  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener('message', this.onMessage);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pagehide', this.onPageHide);
    this.track('video_load');
  }

  /** Queue one event. */
  track(
    event: AnalyticsEventName,
    properties: AnalyticsEventProperties = {},
    playheadMs: number | null = null,
  ): void {
    if (this.ended) return;
    if (this.queue.length >= MAX_QUEUE) this.queue.shift(); // drop oldest
    this.queue.push({
      message_id: uuidv4(),
      view_id: this.viewId,
      session_id: this.sessionId,
      anonymous_id: null, // server-derived
      identified_id: this.identifiedId, // opaque launch-context actor (or null)
      registration: this.registration,
      sig: this.sig,
      seq: this.seq++,
      event,
      viewer_time: Date.now(),
      playhead_ms: playheadMs,
      share_token: this.cfg.shareToken,
      project_id: this.cfg.projectId,
      variant: this.cfg.variant,
      properties,
      context: this.buildContext(),
    });
    if (this.queue.length >= FLUSH_SIZE) this.flush();
    else this.scheduleFlush();
  }

  /** Mark a whole second of media as watched once. Seeks do NOT back-fill. */
  tickSecond(playheadSec: number): void {
    const s = Math.floor(playheadSec);
    if (!Number.isFinite(s) || s < 0 || s === this.lastTickSec) return;
    this.lastTickSec = s;
    this.coverage[s] = (this.coverage[s] ?? 0) + 1;
  }

  /**
   * Feed playhead + total duration + play state so the spine can derive honest
   * watch metrics on view_end: furthest position, actual playing time, and the
   * coverage-based "% viewed" (seek-proof — jumping to the end can't inflate it).
   *
   * `durationMs` MUST be the narration clock (`state.displayDuration`), and
   * `tickSecond` MUST be fed the matching display time. The raw engine timeline
   * includes the 120s VCC hold ceiling on every interactive slide plus the gate
   * windows a solved learner never hears, so measuring against it under-reports
   * watch by ~3x — the 7.EE.A.1 pilot is 849s raw against 251s of narration.
   * Callers: vplay `driveSpine`, web-ui `useVideoAnalytics.onStateChange`.
   */
  progress(playheadMs: number, durationMs: number, isPlaying: boolean): void {
    if (durationMs > 0) this.durationMs = durationMs;
    if (playheadMs > this.maxPlayheadMs) this.maxPlayheadMs = playheadMs;
    const now = Date.now();
    if (isPlaying && this.playingSince === null) this.playingSince = now;
    else if (!isPlaying && this.playingSince !== null) {
      this.playingMs += now - this.playingSince;
      this.playingSince = null;
    }
  }

  /** Fraction of the playhead range [startSec, startSec+durSec) actually watched. */
  coverageRatio(startSec: number, durSec: number): number {
    if (durSec <= 0) return 0;
    const a = Math.max(0, Math.floor(startSec));
    const b = Math.floor(startSec + durSec);
    let covered = 0;
    for (let s = a; s < b; s++) if ((this.coverage[s] ?? 0) > 0) covered++;
    return Math.min(1, covered / durSec);
  }

  /** Flush + emit the terminal `view_end` with the final coverage vector. */
  stop(): void {
    this.finalize('stop');
  }

  // ── internals ────────────────────────────────────────────────────────────

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => this.flush(), FLUSH_MS);
  }

  private flush(useBeacon = false): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0) return;
    const batch = this.queue;
    this.queue = [];
    const body = JSON.stringify({ batch, sent_at: Date.now() });
    if (!this.send(body, useBeacon)) {
      // Re-queue (front) for the next flush; capped by MAX_QUEUE on push.
      this.queue = batch.concat(this.queue);
    }
  }

  private send(body: string, useBeacon: boolean): boolean {
    try {
      if (useBeacon && typeof navigator.sendBeacon === 'function') {
        return navigator.sendBeacon(this.cfg.collectUrl, new Blob([body], { type: 'text/plain' }));
      }
      void fetch(this.cfg.collectUrl, {
        method: 'POST',
        body,
        keepalive: true,
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain' },
      }).catch(() => {
        /* swallowed — analytics never disrupts playback */
      });
      return true;
    } catch {
      return false;
    }
  }

  private finalize(reason: string): void {
    if (this.ended) return;
    // Close the open playing interval so playingMs is complete.
    if (this.playingSince !== null) {
      this.playingMs += Date.now() - this.playingSince;
      this.playingSince = null;
    }
    // Coverage-based "% viewed" (seek-proof) + the furthest playhead reached.
    // Both the vector and the denominator are on the narration clock, so this is
    // "how much of the lesson did they actually hear" — the number a teacher
    // reads — rather than a fraction of a timeline padded with gate holds.
    const seconds = Array.from({ length: this.coverage.length }, (_, i) => this.coverage[i] ?? 0);
    const watchedSec = seconds.filter((s) => s > 0).length;
    const totalSec = Math.max(1, Math.round(this.durationMs / 1000));
    const percentViewed = Math.min(100, Math.round((watchedSec / totalSec) * 100));
    // Emit view_end BEFORE flipping the ended guard (track() short-circuits once ended).
    this.track(
      'view_end',
      {
        reason,
        seconds,
        percent_viewed: percentViewed,
        playing_ms: this.playingMs,
        watch_ms: this.playingMs,
        duration_ms: this.durationMs,
        completed: percentViewed >= 95,
      },
      this.maxPlayheadMs || null,
    );
    this.ended = true;
    this.flush(true); // beacon
    this.teardown();
  }

  private teardown(): void {
    window.removeEventListener('message', this.onMessage);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pagehide', this.onPageHide);
  }

  private handleVisibility(): void {
    if (document.visibilityState === 'hidden') {
      // Beacon-flush on hide (the reliable mobile unload signal); the view is
      // NOT ended — the user may return. pagehide finalizes view_end.
      this.track('video_tab_away');
      this.flush(true);
    } else {
      this.track('video_tab_back');
    }
  }

  private handlePostMessage(e: MessageEvent): void {
    if (!this.allowedMessageOrigins.has(e.origin)) return;
    if (!isSlideAnalyticsMessage(e.data)) return;
    const m = e.data as SlideAnalyticsMessage;
    this.track(
      m.type,
      {
        slide_id: m.slide_id,
        interaction_id: m.interaction_id,
        interaction_type: m.interaction_type,
        ...m.payload,
      },
      m.playhead_ms,
    );
  }

  private buildContext(): AnalyticsContext {
    if (this.ctxCache) return this.ctxCache;
    const nav = navigator as Navigator & {
      userAgentData?: { platform: string; mobile: boolean; brands: Array<{ brand: string; version: string }> };
      connection?: { effectiveType?: string; downlink?: number };
    };

    const framed = window.self !== window.top;
    let embedOrigin: string | undefined;
    if (framed) {
      try {
        const ao = (window.location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins;
        if (ao && ao.length > 0) embedOrigin = ao[0];
        else if (document.referrer) embedOrigin = new URL(document.referrer).origin;
      } catch {
        /* referrer may be opaque */
      }
    }

    const q = new URLSearchParams(window.location.search);
    const utm = (k: string) => q.get(k) || undefined;

    this.ctxCache = {
      page: {
        url: this.cfg.pageUrl ?? window.location.href,
        referrer: document.referrer || undefined,
        embed: framed,
        embed_origin: embedOrigin,
      },
      campaign: {
        source: utm('utm_source'),
        medium: utm('utm_medium'),
        campaign: utm('utm_campaign'),
        term: utm('utm_term'),
        content: utm('utm_content'),
      },
      screen: { w: window.screen.width, h: window.screen.height, dpr: window.devicePixelRatio || 1 },
      viewport: { w: window.innerWidth, h: window.innerHeight },
      locale: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      uaData: nav.userAgentData
        ? { platform: nav.userAgentData.platform, mobile: nav.userAgentData.mobile, brands: nav.userAgentData.brands }
        : undefined,
      network: nav.connection
        ? { effectiveType: nav.connection.effectiveType, downlink: nav.connection.downlink }
        : undefined,
    };
    return this.ctxCache;
  }
}
